import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Flame, Gem, Sun, ZoomIn } from 'lucide-react';
import { ASSETS } from '../assets/assets';
import { SectionTitle } from './ChineseDecor';
import { getLocalizedText } from '../cms/localization';
import type { SupportedLang } from '../cms/localization';
import type { LocalizedText } from '../../shared/types';
import TechSynergySection from '../sections/TechSynergySection';
import Lightbox from '../components/Lightbox';

interface Tech {
  tag: LocalizedText;
  title: LocalizedText;
  summary: LocalizedText;
  desc: LocalizedText;
  image?: string;
}

const techVisuals = [
  { icon: <Gem className="w-7 h-7" />, accent: 'text-brand-gold', ring: 'border-brand-gold/40' },
  { icon: <Sun className="w-7 h-7" />, accent: 'text-brand-cinnabar', ring: 'border-brand-cinnabar/40' },
  { icon: <Flame className="w-7 h-7" />, accent: 'text-brand-golddeep', ring: 'border-brand-golddeep/40' },
];

interface PrinciplesProps {
  content: Record<string, unknown>;
}

const Principles: React.FC<PrinciplesProps> = ({ content }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as SupportedLang;
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  const eyebrow = getLocalizedText(content.eyebrow, lang, t('principle.eyebrow'));
  const title = getLocalizedText(content.title, lang, t('principle.title'));
  const lead = getLocalizedText(content.lead, lang, t('principle.lead'));
  const leadImage = (content.leadImage as string) || ASSETS.warmStones;
  const techTitle = getLocalizedText(content.techTitle, lang, t('principle.tech_title'));

  const techs = (content.techs as Tech[] | undefined) || [];

  return (
    <section id="principle" className="relative py-12 md:py-16 bg-brand-paper overflow-hidden">
      <div className="absolute inset-0 bg-cloud opacity-40 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle eyebrow={eyebrow} title={title} />

        {/* Lead with warm-glow stone visual */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mt-14">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-sm warm-glow">
              <img src={leadImage} alt="" className="w-full h-72 md:h-80 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/40 to-transparent" />
            </div>
          </motion.div>
          <p className="text-brand-ink/75 font-sans leading-loose text-[15px]">{lead}</p>
        </div>

        {/* Three technologies */}
        <h3 className="text-center font-serif text-2xl text-brand-ink mt-20 mb-12 tracking-[0.1em]">
          {techTitle}
        </h3>
        <div className="grid md:grid-cols-3 gap-8">
          {techs.map((tech, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12 }}
              className="relative bg-brand-stone/40 border border-brand-ink/5 p-8 hover:border-brand-gold/40 transition-colors group"
            >
              {/* index seal */}
              <span className="absolute top-6 right-6 w-9 h-9 flex items-center justify-center bg-brand-cinnabar text-brand-paper font-serif text-sm rounded-sm rotate-3 z-10">
                {['壹', '貳', '叄'][i]}
              </span>
              {tech.image && (
                <div
                  className="aspect-[4/3] mb-6 overflow-hidden bg-brand-ink/5 cursor-zoom-in relative"
                  onClick={() => setLightboxSrc(tech.image)}
                  role="button"
                  aria-label="放大查看圖片"
                >
                  <img src={tech.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-brand-ink/70 text-brand-paper text-xs px-2.5 py-1.5 rounded-sm pointer-events-none opacity-80 group-hover:opacity-100 transition-opacity">
                    <ZoomIn className="w-3.5 h-3.5" />
                    點擊放大
                  </div>
                </div>
              )}
              <div className={`w-14 h-14 rounded-full border ${techVisuals[i].ring} flex items-center justify-center ${techVisuals[i].accent} mb-6 group-hover:scale-110 transition-transform`}>
                {techVisuals[i].icon}
              </div>
              <span className="inline-block text-[11px] font-sans tracking-widest text-brand-gold uppercase mb-2">
                {getLocalizedText(tech.tag, lang)}
              </span>
              <h4 className="font-serif text-xl text-brand-ink mb-3">
                {getLocalizedText(tech.title, lang)}
              </h4>
              <p className="text-brand-cinnabar font-serif text-sm mb-4 leading-snug">
                {getLocalizedText(tech.summary, lang)}
              </p>
              <p className="text-brand-ink/65 text-[13px] font-sans leading-relaxed">
                {getLocalizedText(tech.desc, lang)}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Synergy flow + pain points — new animated design */}
        <TechSynergySection content={content} />
      </div>

      <Lightbox
        src={lightboxSrc || ''}
        isOpen={!!lightboxSrc}
        onClose={() => setLightboxSrc(null)}
      />
    </section>
  );
}

export default Principles;
